import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Skeleton } from '../primitives/skeleton';
import { cn } from '../lib/cn';

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  loading?: boolean;
  /** Shown when there are no rows (use EmptyState) */
  empty?: ReactNode;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData) => string;
  /** On phones, show each row as a card instead of a sideways-scrolling table */
  renderCard?: (row: TData) => ReactNode;
  /** `cards` shows the cards at every width, e.g. as the list beside a MasterDetail panel */
  layout?: 'auto' | 'cards';
  /** The row shown in a details panel, highlighted (needs `getRowId`) */
  selectedId?: string | null;
  className?: string;
}

/** Sortable table with loading and empty states. Scrolls sideways inside itself on phones. */
export function DataTable<TData>({
  columns,
  data,
  loading = false,
  empty,
  onRowClick,
  getRowId,
  renderCard,
  layout = 'auto',
  selectedId,
  className,
}: DataTableProps<TData>) {
  const cardsOnly = layout === 'cards' && renderCard !== undefined;
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId,
  });

  if (!loading && data.length === 0 && empty) return <>{empty}</>;

  const cards = renderCard && (
    <ul className={cn('grid gap-2', !cardsOnly && 'md:hidden')}>
      {loading
        ? Array.from({ length: 3 }, (_, i) => (
            <li key={`skeleton-${i}`} className="grid gap-2 rounded-xl border border-border bg-surface p-4">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </li>
          ))
        : table.getRowModel().rows.map((row) => <li key={row.id}>{renderCard(row.original)}</li>)}
    </ul>
  );

  if (cardsOnly) return cards;

  return (
    <>
      {cards}
      <div
        className={cn(
          'overflow-x-auto rounded-xl border border-border bg-surface',
          renderCard && 'hidden md:block',
          className,
        )}
      >
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => {
                  const sortable = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className="whitespace-nowrap px-4 py-2.5"
                      aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {header.isPlaceholder ? null : sortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-50" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 5 }, (_, i) => (
                  <tr key={`skeleton-${i}`} className="border-t border-border">
                    {columns.map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full max-w-40" />
                      </td>
                    ))}
                  </tr>
                ))
              : table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    aria-selected={selectedId !== undefined ? row.id === selectedId : undefined}
                    className={cn(
                      'border-t border-border',
                      onRowClick && 'cursor-pointer hover:bg-muted/60',
                      row.id === selectedId && 'bg-primary/10 hover:bg-primary/10',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-2.5 align-top">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export type { ColumnDef };
